import type { CatalogEntry, Currency, ItemType, RewardBundle } from '@vm/shared';
import { ARMOR_STYLES, SKIN_PRESETS } from '../characters';
import { BANNERS, CAMOS, DEATH_EFFECTS, type CosmeticDef } from '../cosmetics';
import { GADGETS } from '../gadgets';
import { PET_STYLES, PETS } from '../pets';
import { SCOPES } from '../scopes';
import { TRACERS } from '../tracers';
import { SHOP_WEAPONS } from '../weapons';
import { MAX_LEVEL } from './levels';

export const ITEM_TYPES: readonly ItemType[] = ['weapon', 'gadget', 'skin', 'armor', 'camo', 'tracer', 'scope', 'deathfx', 'banner', 'title', 'pet', 'petstyle'];

/** Stable key of an item (`${type}:${id}`), used by price overrides and inventory lookups. */
export const itemKey = (type: ItemType, id: string): string => `${type}:${id}`;

/** Percentage of an item's price paid back (same currency) when a reward grants an item already owned. */
export const DUPLICATE_COMPENSATION_PCT = 25;

function cosmeticSource(def: CosmeticDef): CatalogEntry['source'] {
  switch (def.source) {
    case 'mastery':
      // Weapon mastery camos are earned per weapon (kills) — closest catalog source is 'achievement'.
      return 'achievement';
    case undefined:
      return def.price > 0 ? 'shop' : 'starter';
    default:
      return def.source;
  }
}

/** Gadgets and pets carry no rarity in content; derive one from the coin price for display (same bands as weapons). */
function coinRarity(price: number): CatalogEntry['rarity'] {
  return price >= 3500 ? 'epic' : price >= 1500 ? 'rare' : 'common';
}

function basic(
  type: ItemType,
  def: { id: string; name: string; rarity: CatalogEntry['rarity']; price: number; currency: Currency; unlockLevel: number; source?: string },
): CatalogEntry {
  return {
    type,
    id: def.id,
    name: def.name,
    rarity: def.rarity,
    price: def.price,
    currency: def.currency,
    unlockLevel: def.unlockLevel,
    // Pass and event skins/armor say so; everything else is sold, or a starter when free.
    source: def.source === 'battlepass' || def.source === 'event' ? def.source : def.price === 0 && def.unlockLevel <= 1 ? 'starter' : 'shop',
  };
}

/**
 * Every ownable item defined in content with its base price and how it is obtained. Mode-only
 * items (builder tool, zombie claws/skin) are excluded. The API layers admin overrides on top.
 */
export function contentCatalog(): CatalogEntry[] {
  const out: CatalogEntry[] = [];
  for (const w of SHOP_WEAPONS) out.push(basic('weapon', w));
  for (const g of GADGETS) out.push(basic('gadget', { ...g, rarity: coinRarity(g.price) }));
  // Pets are power (loadout slot): unlocked by level, bought with coins. The free level-1 pet is a starter.
  for (const p of PETS) out.push(basic('pet', { ...p, rarity: coinRarity(p.price) }));
  for (const s of SKIN_PRESETS) if (s.unlockLevel <= MAX_LEVEL) out.push(basic('skin', s));
  for (const a of ARMOR_STYLES) out.push(basic('armor', a));
  const cosmetic = (type: ItemType, list: CosmeticDef[]): void => {
    for (const d of list) out.push({ ...basic(type, d), source: cosmeticSource(d) });
  };
  cosmetic('camo', CAMOS);
  cosmetic('tracer', TRACERS);
  cosmetic('scope', SCOPES);
  cosmetic('deathfx', DEATH_EFFECTS);
  cosmetic('banner', BANNERS);
  cosmetic('petstyle', PET_STYLES);
  return out;
}

let baseIndex: Map<string, CatalogEntry> | null = null;
/** Base (content) catalog entry for an item, or undefined when the item does not exist. */
export function contentItem(type: ItemType, id: string): CatalogEntry | undefined {
  if (!baseIndex) baseIndex = new Map(contentCatalog().map((e) => [itemKey(e.type, e.id), e]));
  return baseIndex.get(itemKey(type, id));
}

/** Items every account owns from the start. */
export function starterItems(): { type: ItemType; id: string }[] {
  return contentCatalog()
    .filter((e) => e.source === 'starter')
    .map((e) => ({ type: e.type, id: e.id }));
}

/** Compensation granted instead of a reward item the player already owns. */
export function duplicateCompensation(entry: Pick<CatalogEntry, 'price' | 'currency'>): RewardBundle {
  const amount = Math.floor((entry.price * DUPLICATE_COMPENSATION_PCT) / 100);
  if (amount <= 0) return {};
  return entry.currency === 'gems' ? { gems: amount } : { coins: amount };
}
